/**
 * Manila wall-clock helpers. Every operating-hours, lead-time, and travel-table
 * rule in the ASAP/Scheduled flows is stated in Asia/Manila local time, so the
 * conversion lives in exactly one place — a second copy would drift.
 *
 * `Intl` keeps this Deno-importable; no Node APIs, no tz database bundled.
 */

export const OPERATING_TIME_ZONE = "Asia/Manila";

// `en-CA` yields ISO-shaped YYYY-MM-DD parts; h23 keeps midnight at hour 00.
const formatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: OPERATING_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export interface ManilaInstant {
  /** Manila calendar date, `YYYY-MM-DD`. */
  date: string;
  /** Hour of the Manila day, 0–23. */
  hour: number;
  /** Minutes since Manila midnight, 0–1439. */
  minuteOfDay: number;
}

/** Parse an ISO instant, rejecting garbage rather than silently yielding NaN. */
export function parseInstant(iso: string, field: string): number {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) throw new RangeError(`${field} must be a valid ISO instant`);
  return ms;
}

/** Wall-clock view of an instant in Manila, whatever the host time zone is. */
export function toManila(iso: string, field: string): ManilaInstant {
  const parts = formatter.formatToParts(new Date(parseInstant(iso, field)));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const hour = Number(part("hour"));
  return {
    date: `${part("year")}-${part("month")}-${part("day")}`,
    hour,
    minuteOfDay: hour * 60 + Number(part("minute")),
  };
}
